// PATCH /api/line-items/{id} (design §8.2). Writes `source='user'`,
// `confidence=NULL` server-side and bumps `version`; the response's
// `apply_to_similar` block carries REAL counts (`this_month`, `all_time`)
// from a live query, or `null` when the item has no merchant -- replacing
// the apply-to-similar dialog's previously-hardcoded option details (D4).
// A version mismatch is `409 changed_since`; this hook never retries
// silently -- it surfaces a typed `ApiError` so the caller can render the
// "this changed elsewhere -- reload" state (design §S6 step 6).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope, isErrorEnvelope } from '../envelope';

export class ApiError extends Error {
  code: string;
  details: unknown;
  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

function toApiError(err: unknown): ApiError {
  if (isAxiosError(err) && isErrorEnvelope(err.response?.data)) {
    const { error } = err.response!.data as { error: { code: string; message: string; details?: unknown } };
    return new ApiError(error.code, error.message, error.details);
  }
  return new ApiError('unknown', err instanceof Error ? err.message : 'Request failed');
}

export const applyToSimilarOptionSchema = z.object({
  merchant_id: z.string(),
  merchant_display: z.string(),
  this_month: z.number(),
  all_time: z.number(),
});
export type ApplyToSimilarOption = z.infer<typeof applyToSimilarOptionSchema>;

export const lineItemPatchRequestSchema = z.object({
  version: z.number(),
  category_id: z.string().nullable().optional(),
  is_business: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
});
export type LineItemPatchRequest = z.infer<typeof lineItemPatchRequestSchema>;

// The PATCH response's `line_item` is `LineItemDetail`
// (backend/schemas/line_items.py) -- a DIFFERENT, wider shape than
// `LineItemOut`/`useTransactions.lineItemSchema` (no `confidence`; adds
// `transaction_id`, `account_id`, `posted_at`, `currency`, `merchant_id`,
// `merchant_display`, `memo_raw`, `tags`). Reusing `lineItemSchema` here was
// the ENG-R1-003 class of bug: it requires a `confidence` key the backend
// never sends on this endpoint, so every real PATCH would throw a ZodError.
export const lineItemDetailSchema = z.object({
  line_item_id: z.string(),
  transaction_id: z.string(),
  account_id: z.string(),
  posted_at: z.string(),
  amount: z.string(),
  currency: z.string(),
  merchant_id: z.string().nullable(),
  merchant_display: z.string().nullable(),
  memo_raw: z.string().nullable(),
  category_id: z.string().nullable(),
  category_slug: z.string().nullable(),
  is_business: z.boolean(),
  tags: z.array(z.string()),
  source: z.string(),
  version: z.number(),
});
export type LineItemDetail = z.infer<typeof lineItemDetailSchema>;

export const lineItemPatchResponseSchema = z.object({
  line_item: lineItemDetailSchema,
  // `LineItemPatchData.audit_id` is `int | None` server-side
  // (backend/schemas/line_items.py) -- not a string. A no-op PATCH (nothing
  // actually changed) returns `null` (ENG-R1-003).
  audit_id: z.number().nullable(),
  batch_id: z.string(),
  apply_to_similar: applyToSimilarOptionSchema.nullable(),
});
export type LineItemPatchResponse = z.infer<typeof lineItemPatchResponseSchema>;

export function useLineItemPatch() {
  const queryClient = useQueryClient();
  return useMutation<LineItemPatchResponse, ApiError, { lineItemId: string; input: LineItemPatchRequest }>({
    mutationFn: async ({ lineItemId, input }) => {
      try {
        const response = await client.patch(`/api/line-items/${lineItemId}`, input);
        return parseEnvelope(lineItemPatchResponseSchema, response.data).data;
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      // A category/is_business write shifts Business and Categories view
      // totals; invalidate both spend aggregation query prefixes so those
      // views don't keep showing pre-flip totals (ENG-R1-005).
      queryClient.invalidateQueries({ queryKey: ['spend-by-category'] });
      queryClient.invalidateQueries({ queryKey: ['spend-by-merchant'] });
    },
  });
}

/** True when the failure is the version-conflict case the UI must not silently retry. */
export function isChangedSinceError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === 'changed_since';
}
